/**
 * FileUpload - Drag-and-drop file upload component
 *
 * Implements HOME-10: Attachments support PDF, DOC/DOCX, images, and video (100MB max)
 *
 * Features:
 * - Drag-and-drop zone with visual feedback
 * - Click to browse files
 * - File type validation
 * - File size validation (100MB limit)
 * - Error display with auto-dismiss
 */

import {
  useState,
  useRef,
  useCallback,
  type DragEvent,
  type ChangeEvent,
} from 'react';
import { usePermissions } from '../../hooks/usePermissions';

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Allowed MIME types per HOME-10 requirement.
 * Organized by category for easier maintenance.
 */
const ALLOWED_TYPES = {
  image: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  document: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
  video: ['video/mp4', 'video/webm', 'video/quicktime'],
};

/** Flat array of all allowed MIME types for validation */
const ALL_ALLOWED_TYPES = [
  ...ALLOWED_TYPES.image,
  ...ALLOWED_TYPES.document,
  ...ALLOWED_TYPES.video,
];

/** Maximum file size: 100MB (per HOME-10) */
const MAX_FILE_SIZE = 100 * 1024 * 1024;

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface FileUploadProps {
  /** Callback when valid files are selected */
  onUpload: (files: File[]) => void;
  /** Whether the upload zone is disabled */
  disabled?: boolean;
  /** Additional CSS class name */
  className?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function FileUpload({
  onUpload,
  disabled,
  className = '',
}: FileUploadProps) {
  // Check user permissions (RBAC - HOME-12)
  const permissions = usePermissions();

  // State for drag visual feedback
  const [isDragging, setIsDragging] = useState(false);
  // State for validation error display
  const [error, setError] = useState<string | null>(null);
  // Ref to hidden file input
  const fileInputRef = useRef<HTMLInputElement>(null);

  /**
   * Validate files against type and size constraints.
   * Returns array of valid files, sets error for invalid ones.
   */
  const validateFiles = useCallback((files: FileList | File[]): File[] => {
    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      // Check MIME type
      if (!ALL_ALLOWED_TYPES.includes(file.type)) {
        errors.push(`${file.name}: Unsupported file type`);
        return;
      }

      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name}: File exceeds 100MB limit`);
        return;
      }

      validFiles.push(file);
    });

    // Show errors if any, auto-dismiss after 5 seconds
    if (errors.length > 0) {
      setError(errors.join('. '));
      setTimeout(() => setError(null), 5000);
    }

    return validFiles;
  }, []);

  /**
   * Handle drag over event - show visual feedback.
   */
  const handleDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!disabled) {
        setIsDragging(true);
      }
    },
    [disabled]
  );

  /**
   * Handle drag leave event - hide visual feedback.
   */
  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  /**
   * Handle file drop - validate and upload files.
   */
  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragging(false);

      if (disabled) return;

      const files = e.dataTransfer.files;
      const validFiles = validateFiles(files);
      if (validFiles.length > 0) {
        onUpload(validFiles);
      }
    },
    [disabled, onUpload, validateFiles]
  );

  /**
   * Handle file selection via input - validate and upload files.
   */
  const handleFileSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files || disabled) return;

      const validFiles = validateFiles(e.target.files);
      if (validFiles.length > 0) {
        onUpload(validFiles);
      }

      // Reset input so same file can be selected again
      e.target.value = '';
    },
    [disabled, onUpload, validateFiles]
  );

  /**
   * Handle click on drop zone - open file browser.
   */
  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  // Hide upload component if user doesn't have upload permission
  if (!permissions.canUploadFiles) {
    return null;
  }

  return (
    <div className={`file-upload ${className}`}>
      {/* Drop zone with visual states */}
      <div
        className={`drop-zone ${isDragging ? 'dragging' : ''} ${disabled ? 'disabled' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        {/* Hidden file input triggered by click */}
        <input
          ref={fileInputRef}
          type="file"
          accept={ALL_ALLOWED_TYPES.join(',')}
          onChange={handleFileSelect}
          multiple
          hidden
          disabled={disabled}
        />

        {/* Upload icon */}
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <path d="M12 16V4m0 0L8 8m4-4l4 4" />
          <path d="M2 17v1a4 4 0 004 4h12a4 4 0 004-4v-1" />
        </svg>

        {/* Instructions text */}
        <p>Drop files here or click to upload</p>
        <span>PDF, DOC, DOCX, images, video (max 100MB)</span>
      </div>

      {/* Error display */}
      {error && <div className="upload-error">{error}</div>}

      {/* Inline styles for file upload component */}
      <style>{`
        .file-upload {
          position: relative;
        }

        .drop-zone {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 20px;
          border: 2px dashed var(--color-border);
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.15s;
          text-align: center;
        }

        .drop-zone:hover {
          border-color: var(--color-accent);
          background: var(--color-primary-soft);
        }

        .drop-zone.dragging {
          border-color: var(--color-accent);
          background: var(--color-primary-soft-strong);
        }

        .drop-zone.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .drop-zone svg {
          width: 32px;
          height: 32px;
          color: var(--color-text-muted);
          margin-bottom: 8px;
        }

        .drop-zone p {
          font-size: 14px;
          color: var(--color-text-secondary);
          margin: 0 0 4px;
        }

        .drop-zone span {
          font-size: 12px;
          color: var(--color-text-muted);
        }

        .upload-error {
          margin-top: 8px;
          padding: 8px 12px;
          background: var(--color-error-soft);
          border-radius: 4px;
          color: var(--color-error);
          font-size: 12px;
        }
      `}</style>
    </div>
  );
}
